import { useEffect, useId, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import Button from '../core/Button';
import IconButton from '../core/IconButton';
import { cn } from '@/helpers/classnames';
import { formatDate, formatDateTime } from '@/helpers/formatting';
import { PeriodDateRange } from '@/components/PeriodDateRange';
import { toPeriodDate } from '@/helpers/periodEndDate';
import { getPeriodStatus } from '@/helpers/periodStatus';
import { useTenantStaticTicketTitles } from '@/hooks/useStaticTicketTitles';
import { IconChevronRight, IconTrash } from '@/icons';
import { collator } from '@/utils/collator';
import { FreePeriodBadge } from './FreePeriodBadge';
import { PeriodStatusBadge } from './PeriodStatusBadge';
import { PeriodTicketKind } from './PeriodTicketKind';
import { SelfManagedPeriodChip } from './SelfManagedPeriodChip';
import type { PeriodConfigurationGroup } from '@/helpers/periodConfiguration';
import type {
  CollectionTicketClaimDTO,
  OrganizationDTO,
} from '@/api/trafikportalen';

interface TenantUpcomingPeriodGroupsProps {
  groups: PeriodConfigurationGroup[];
  organizationsById: Map<string, OrganizationDTO>;
  canEdit: boolean;
  selectedKey: string | null;
  onSelect: (claim: CollectionTicketClaimDTO) => void;
  onSelectOrganizations: (organizationIds: string[]) => void;
  onEdit: (claims: CollectionTicketClaimDTO[]) => void;
  onDelete: (claims: CollectionTicketClaimDTO[]) => void;
  emptyMessage?: ReactNode;
}

interface PeriodOrganization {
  id: string;
  name: string;
  selfManaged: boolean;
}

const periodRange = (claim: CollectionTicketClaimDTO) =>
  `${claim.valid_from ? toPeriodDate(claim.valid_from) : '—'} – ${
    claim.valid_to ? toPeriodDate(claim.valid_to, 'end') : '—'
  }`;

const auditBounds = (values: string[]) => {
  const instants = values
    .flatMap((value) => {
      const instant = DateTime.fromISO(value);
      return instant.isValid ? [{ value, instant }] : [];
    })
    .sort((a, b) => a.instant.toMillis() - b.instant.toMillis());

  const first = instants[0];
  const last = instants[instants.length - 1];
  if (!first || !last) return null;

  const fromTime = formatDateTime(first.value);
  const toTime = formatDateTime(last.value);
  const single = fromTime === toTime;

  return {
    from: first.value,
    to: last.value,
    single,
    title: single ? fromTime : `${fromTime} – ${toTime}`,
  };
};

const AuditDate = ({
  kind,
  values,
}: {
  kind: 'created' | 'updated';
  values: string[];
}) => {
  const bounds = auditBounds(values);
  if (!bounds) return null;

  const date = bounds.single ? (
    <time dateTime={bounds.from}>{formatDate(bounds.from)}</time>
  ) : (
    <>
      <time dateTime={bounds.from}>{formatDate(bounds.from)}</time>
      {' – '}
      <time dateTime={bounds.to}>{formatDate(bounds.to)}</time>
    </>
  );

  return (
    <span className="whitespace-nowrap" title={bounds.title}>
      {kind === 'created' ? (
        <FormattedMessage defaultMessage="Skapad {date}" values={{ date }} />
      ) : (
        <FormattedMessage
          defaultMessage="Uppdaterad {date}"
          values={{ date }}
        />
      )}
    </span>
  );
};

interface PeriodGroupItemProps {
  group: PeriodConfigurationGroup;
  organizations: PeriodOrganization[];
  range: string;
  expanded: boolean;
  canEdit: boolean;
  onSelect: (claim: CollectionTicketClaimDTO) => void;
  onSelectOrganizations: (organizationIds: string[]) => void;
  onEdit: (claims: CollectionTicketClaimDTO[]) => void;
  onDelete: (claims: CollectionTicketClaimDTO[]) => void;
}

const PeriodGroupItem = ({
  group,
  organizations,
  range,
  expanded,
  canEdit,
  onSelect,
  onSelectOrganizations,
  onEdit,
  onDelete,
}: PeriodGroupItemProps) => {
  const { formatMessage } = useIntl();
  const ticketTitle = useTenantStaticTicketTitles();
  const panelId = useId();
  const itemRef = useRef<HTMLLIElement>(null);
  const template = group.claims[0];

  useEffect(() => {
    if (!expanded) return;
    itemRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [expanded]);

  if (!template) return null;

  const relational = template.relational === true;
  const revokeStrategy = template.revoke_strategy ?? 'none';
  const revokeLabel =
    revokeStrategy === 'revoke'
      ? formatMessage({ defaultMessage: 'Makulera' })
      : formatMessage({ defaultMessage: 'Ingen' });
  const count = organizations.length;
  const status = getPeriodStatus(template);
  const staticTicketName = relational
    ? undefined
    : ticketTitle(template.stina_product_id);

  return (
    <li
      ref={itemRef}
      aria-label={formatMessage(
        {
          defaultMessage:
            '{range}, {count, plural, one {# organisation} other {# organisationer}}{free, select, yes {, kostnadsfri} other {}}',
        },
        {
          range,
          count,
          free: template.non_invoiced ? 'yes' : 'no',
        },
      )}
      className={cn(
        'flex flex-col gap-3 rounded-md border p-4',
        expanded
          ? 'border-primary-600 bg-primary-50'
          : 'border-background-300 bg-background-50',
      )}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={expanded ? panelId : undefined}
        onClick={() => onSelect(template)}
        className="-m-2 flex w-[calc(100%+1rem)] items-start gap-2 rounded-md p-2 text-left hover:bg-background-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
      >
        <span
          className={cn(
            'mt-0.5 shrink-0 transition-transform',
            expanded && 'rotate-90',
          )}
          aria-hidden="true"
        >
          <IconChevronRight className="size-5" />
        </span>
        <span className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <PeriodDateRange
              from={template.valid_from}
              to={template.valid_to}
              className="font-medium"
            />
            <PeriodStatusBadge status={status} />
            {template.non_invoiced && <FreePeriodBadge />}
          </span>
          <span className="text-text-700">
            <FormattedMessage
              defaultMessage="Makulering: {strategy}"
              values={{ strategy: revokeLabel }}
            />
            {' · '}
            <PeriodTicketKind
              relational={relational}
              staticTicketName={staticTicketName}
            />
          </span>
          <span className="text-text-700">
            <FormattedMessage
              defaultMessage="{count, plural, one {# organisation} other {# organisationer}}"
              values={{ count }}
            />
          </span>
        </span>
      </button>
      {expanded && (
        <div id={panelId} className="flex flex-col gap-3 pl-7">
          <ul
            aria-label={formatMessage({
              defaultMessage: 'Organisationer som använder perioden',
            })}
            className="flex flex-col gap-1"
          >
            {organizations.map((organization) => (
              <li
                key={organization.id}
                className="flex flex-wrap items-center gap-2"
              >
                <span>{organization.name}</span>
                {organization.selfManaged && <SelfManagedPeriodChip />}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-x-3 text-text-700">
            <AuditDate
              kind="created"
              values={group.claims.map((claim) => claim.created_at)}
            />
            <AuditDate
              kind="updated"
              values={group.claims.map((claim) => claim.updated_at)}
            />
          </div>
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="filled"
                onClick={() => onSelectOrganizations(group.organizationIds)}
              >
                <FormattedMessage defaultMessage="Markera organisationerna" />
              </Button>
              <Button variant="filled" onClick={() => onEdit(group.claims)}>
                {status === 'upcoming' ? (
                  <FormattedMessage defaultMessage="Ändra datum" />
                ) : (
                  <FormattedMessage defaultMessage="Ändra slutdatum" />
                )}
              </Button>
              {status === 'upcoming' && (
                <IconButton
                  variant="filled"
                  color="red"
                  className="ml-auto"
                  onClick={() => onDelete(group.claims)}
                  aria-label={formatMessage({
                    defaultMessage: 'Radera period',
                  })}
                  icon={<IconTrash className="size-4" />}
                />
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export const TenantUpcomingPeriodGroups = ({
  groups,
  organizationsById,
  canEdit,
  selectedKey,
  onSelect,
  onSelectOrganizations,
  onEdit,
  onDelete,
  emptyMessage,
}: TenantUpcomingPeriodGroupsProps) => {
  const { formatMessage } = useIntl();

  const rows = useMemo(
    () =>
      groups.map((group) => {
        const template = group.claims[0];
        const range = template ? periodRange(template) : '—';
        const organizations = group.organizationIds
          .map((id) => {
            const organization = organizationsById.get(id);
            return {
              id,
              name: organization?.name ?? id,
              selfManaged: organization?.selfmanaged_school_claims === true,
            };
          })
          .sort((a, b) => collator.compare(a.name, b.name));
        return { group, range, organizations };
      }),
    [groups, organizationsById],
  );

  if (rows.length === 0) {
    return (
      <p className="italic text-text-500">
        {emptyMessage ?? (
          <FormattedMessage defaultMessage="Inga pågående eller kommande perioder." />
        )}
      </p>
    );
  }

  return (
    <ul
      aria-label={formatMessage({ defaultMessage: 'Aktuella perioder' })}
      className="flex flex-col gap-2"
    >
      {rows.map(({ group, range, organizations }) => (
        <PeriodGroupItem
          key={group.key}
          group={group}
          organizations={organizations}
          range={range}
          expanded={group.key === selectedKey}
          canEdit={canEdit}
          onSelect={onSelect}
          onSelectOrganizations={onSelectOrganizations}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </ul>
  );
};
